(function () {
  'use strict';

  // Replaces Shopify's native /contact submission entirely (so only ONE
  // notification email is ever sent per enquiry) with a call to a custom
  // relay (Vercel + Resend) that emails a nicely formatted version with a
  // clickable phone number, a WhatsApp link, and the Enquiry Source / Page
  // URL fields front and center.
  //
  // Safety net: if the relay call itself fails (network error, relay down),
  // the form is allowed to submit to Shopify natively exactly once, so a
  // lead is never silently lost -- worst case you get Shopify's plain email
  // instead of the branded one, never zero and never both.
  //
  // Duplicate-send hardening: a form can only ever be relayed once, no
  // matter how many 'submit' events land on it (double click, Enter key +
  // click, a stray re-render, etc). This is enforced three separate ways:
  //   1. a permanent per-element flag set the instant a submit is accepted
  //   2. a client-generated idempotency key sent to the relay, which the
  //      relay forwards to Resend -- Resend itself will then never actually
  //      send a second email for that key even if this endpoint somehow
  //      gets hit twice
  //   3. the "show success" DOM update is wrapped so it can never itself
  //      throw into the failure path and trigger an unwanted second send

  var RELAY_URL = 'https://bigsmall-lead-relay.vercel.app/api/lead';
  var submittedForms = new WeakSet();

  function isContactForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    var action = form.getAttribute('action') || '';
    return /\/contact(\b|#|\?|$)/i.test(action);
  }

  function extractFields(form) {
    var fields = {};
    try {
      var data = new FormData(form);
      data.forEach(function (value, key) {
        var match = key.match(/^contact\[(.+)\]$/);
        if (!match) return;
        if (typeof value !== 'string') return;
        if (value.trim() === '') return;
        fields[match[1]] = value;
      });
    } catch (e) {
      // ignore -- if FormData construction fails for any reason, treat as empty
    }
    return fields;
  }

  function makeIdempotencyKey() {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }
    } catch (e) {}
    return 'bs-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
  }

  function injectSuccessStyles() {
    if (document.getElementById('bs-relay-success-style')) return;
    var style = document.createElement('style');
    style.id = 'bs-relay-success-style';
    style.textContent =
      '.bs-relay-success{padding:16px 18px;margin:0 0 16px;border:1px solid #b9dfca;' +
      'border-radius:10px;background:#f1fbf5;color:#165c35;' +
      'font:600 14px/1.5 Lato,Arial,sans-serif;}';
    document.head.appendChild(style);
  }

  function showSuccess(form) {
    // Deliberately isolated in its own try/catch: a DOM error here must
    // never be able to fall through to the caller's .catch() and trigger
    // fallbackSubmit(), which would send a second (native Shopify) email
    // for an enquiry that already sent successfully.
    try {
      injectSuccessStyles();
      var message = document.createElement('div');
      message.className = 'bs-relay-success';
      message.setAttribute('role', 'status');
      message.tabIndex = -1;
      message.textContent = 'Thank you. Your enquiry has been received — our team will get in touch with you shortly.';
      form.hidden = true;
      form.insertAdjacentElement('beforebegin', message);
      message.focus();
    } catch (e) {
      // Even if the success UI itself fails to render, the email has
      // already been sent successfully -- there is nothing to recover here,
      // and above all nothing that should trigger a second send.
    }
  }

  function fallbackSubmit(form) {
    // Native Shopify submission, used only when the relay itself failed.
    // form.submit() does not re-dispatch the 'submit' event, so this can
    // never loop back into this same handler.
    try {
      form.submit();
    } catch (e) {}
  }

  function resetSubmitButton(form) {
    var btn = form.querySelector('button[type="submit"], input[type="submit"]');
    if (btn && btn.dataset.bsOriginalLabel !== undefined) {
      btn.innerHTML = btn.dataset.bsOriginalLabel;
      btn.disabled = false;
    }
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!isContactForm(form)) return;

    // Hard stop: once a form has been accepted for submission once, every
    // subsequent 'submit' event on that same element is ignored outright,
    // regardless of cause.
    if (submittedForms.has(form)) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    submittedForms.add(form);

    event.preventDefault();
    event.stopPropagation();

    var fields = extractFields(form);
    if (!Object.keys(fields).length) {
      fallbackSubmit(form);
      return;
    }

    var btn = form.querySelector('button[type="submit"], input[type="submit"]');
    if (btn) {
      btn.dataset.bsOriginalLabel = btn.innerHTML;
      btn.disabled = true;
      btn.innerHTML = 'Sending\u2026';
    }

    var payload = {
      idempotencyKey: makeIdempotencyKey(),
      enquirySource:
        fields['Enquiry Source'] ||
        fields['Enquiry type'] ||
        fields['Enquiry source'] ||
        document.title,
      pageUrl: fields['Page URL'] || fields['page_url'] || window.location.href,
      fields: fields
    };

    if (!window.fetch) {
      fallbackSubmit(form);
      return;
    }

    fetch(RELAY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (res) {
        if (!res.ok) throw new Error('relay failed');
        showSuccess(form);
      })
      .catch(function () {
        resetSubmitButton(form);
        fallbackSubmit(form);
      });
  }, true);
})();
